import React, { useEffect, useState } from "react";
import {
  Form, Input, InputNumber, Select, Checkbox, Radio, Switch,
  Button, Tabs, Card, Space, message, Divider, Collapse,
} from "antd";
import { PlusOutlined, DeleteOutlined, FileTextOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { OPTION_LISTS, getMyReview, saveMyReview } from "services/holistic";

const { TextArea } = Input;

const opts = (category) =>
  (OPTION_LISTS[category] || []).map((o) => ({ value: o.code, label: o.label }));

// Generic repeating section built on Form.List.
const RepeatList = ({ name, addLabel, itemLabel, renderItem }) => (
  <Form.List name={name}>
    {(fields, { add, remove }) => (
      <div className="flex flex-col gap-3">
        {fields.length > 0 && (
          <Collapse
            defaultActiveKey={fields.map((f) => f.key)}
            items={fields.map(({ key, name: idx, ...rest }) => ({
              key,
              forceRender: true,
              label: `${itemLabel} №${idx + 1}`,
              extra: (
                <DeleteOutlined
                  style={{ color: "#ff4d4f" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    remove(idx);
                  }}
                />
              ),
              children: renderItem(idx, rest),
            }))}
          />
        )}
        <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} block>
          {addLabel}
        </Button>
      </div>
    )}
  </Form.List>
);

function CollegePrepForm() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getMyReview()
      .then((data) => form.setFieldsValue(data))
      .finally(() => setLoading(false));
  }, [form]);

  const persist = (status) => {
    setSaving(true);
    const values = form.getFieldsValue(true);
    saveMyReview(values, status)
      .then(() =>
        message.success(
          status === "SUBMITTED" ? "Амжилттай илгээлээ!" : "Ноорог хадгаллаа!"
        )
      )
      .finally(() => setSaving(false));
  };

  const items = [
    {
      key: "vision",
      label: "Алсын хараа",
      children: (
        <RepeatList
          name="vision"
          itemLabel="Алсын хараа"
          addLabel="Алсын хараа нэмэх"
          renderItem={(idx, rest) => (
            <>
              <Form.Item {...rest} name={[idx, "socialIssue"]} label="Хувь нэмэр оруулахыг хүсдэг салбар, нийгмийн асуудал">
                <TextArea autoSize={{ minRows: 2, maxRows: 14 }} />
              </Form.Item>
              <Form.Item {...rest} name={[idx, "rootCause"]} label="Асуудлыг үүсгэж буй гол хүчин зүйл ба хэрхэн ажигласан">
                <TextArea autoSize={{ minRows: 3, maxRows: 14 }} />
              </Form.Item>
              <Form.Item {...rest} name={[idx, "contribution"]} label="Чи яаж хувь нэмрээ оруулах вэ? (төсөл, сайн дурын/судалгааны ажил)">
                <TextArea autoSize={{ minRows: 3, maxRows: 14 }} />
              </Form.Item>
            </>
          )}
        />
      ),
    },
    {
      key: "passion",
      label: "Хүсэл эрмэлзэл",
      children: (
        <RepeatList
          name="passion"
          itemLabel="Хүсэл эрмэлзэл"
          addLabel="Мэргэжил нэмэх"
          renderItem={(idx, rest) => (
            <>
              <Form.Item {...rest} name={[idx, "profession"]} label="Эзэмшихийг хүсэж буй мэргэжил">
                <TextArea autoSize={{ minRows: 2, maxRows: 14 }} />
              </Form.Item>
              <Form.Item {...rest} name={[idx, "motivation"]} label="Энэ мэргэжлийг сонгох болсон шалтгаан">
                <TextArea autoSize={{ minRows: 3, maxRows: 14 }} />
              </Form.Item>
              <Form.Item {...rest} name={[idx, "experience"]} label="Холбоотой төсөл/туршлага эсвэл туршихаар төлөвлөж буй зүйлс">
                <TextArea autoSize={{ minRows: 3, maxRows: 14 }} />
              </Form.Item>
            </>
          )}
        />
      ),
    },
    {
      key: "extracurriculars",
      label: "ХГҮА",
      children: (
        <RepeatList
          name="extracurriculars"
          itemLabel="ХГҮА"
          addLabel="Үйл ажиллагаа нэмэх"
          renderItem={(idx, rest) => (
            <>
              <Form.Item {...rest} name={[idx, "activityType"]} label="Activity type">
                <Select options={opts("ACTIVITY_TYPE")} showSearch optionFilterProp="label" />
              </Form.Item>
              <Form.Item {...rest} name={[idx, "position"]} label="Албан тушаал / манлайлал">
                <Input maxLength={50} />
              </Form.Item>
              <Form.Item {...rest} name={[idx, "organization"]} label="Байгууллага">
                <Input maxLength={100} />
              </Form.Item>
              <Form.Item {...rest} name={[idx, "description"]} label="Тайлбар">
                <TextArea autoSize={{ minRows: 3, maxRows: 14 }} />
              </Form.Item>
              <Form.Item {...rest} name={[idx, "gradeLevels"]} label="Оролцсон анги">
                <Checkbox.Group options={opts("GRADE_LEVEL")} />
              </Form.Item>
              <Form.Item {...rest} name={[idx, "timing"]} label="Оролцооны цаг">
                <Radio.Group options={opts("EC_TIMING")} />
              </Form.Item>
              <Space>
                <Form.Item {...rest} name={[idx, "hoursPerWeek"]} label="Цаг / 7 хоног">
                  <InputNumber min={0} />
                </Form.Item>
                <Form.Item {...rest} name={[idx, "weeksPerYear"]} label="7 хоног / жил">
                  <InputNumber min={0} />
                </Form.Item>
              </Space>
            </>
          )}
        />
      ),
    },
    {
      key: "honors",
      label: "Шагнал",
      children: (
        <RepeatList
          name="honors"
          itemLabel="Шагнал"
          addLabel="Шагнал нэмэх"
          renderItem={(idx, rest) => (
            <>
              <Form.Item {...rest} name={[idx, "recognitionLevel"]} label="Level of recognition">
                <Radio.Group options={opts("RECOGNITION_LEVEL")} />
              </Form.Item>
              <Form.Item {...rest} name={[idx, "gradeLevels"]} label="Grade level">
                <Checkbox.Group options={opts("GRADE_LEVEL")} />
              </Form.Item>
              <Form.Item {...rest} name={[idx, "description"]} label="Тайлбар">
                <TextArea autoSize={{ minRows: 3, maxRows: 14 }} />
              </Form.Item>
            </>
          )}
        />
      ),
    },
    {
      key: "recLetters",
      label: "Тодорхойлох захидал",
      children: (
        <>
          <RepeatList
            name="recLetters"
            itemLabel="Recommendation letter"
            addLabel="Багш нэмэх"
            renderItem={(idx, rest) => (
              <>
                <Form.Item {...rest} name={[idx, "teacher"]} label="Rec letter авах төлөвлөгөөтэй хичээлийн багш">
                  <Input />
                </Form.Item>
                <Form.Item {...rest} name={[idx, "memories"]} label="Тухайн багштай холбоотой гурван дурсамж, давуу тал">
                  <TextArea autoSize={{ minRows: 2, maxRows: 14 }} />
                </Form.Item>
              </>
            )}
          />
          <Divider />
          <Form.Item name="bestSubject" label="Хамгийн сайн хичээл">
            <Input />
          </Form.Item>
          <Form.Item name="teacherStudentRelation" label="Багш шавийн харилцаа">
            <TextArea autoSize={{ minRows: 2, maxRows: 14 }} />
          </Form.Item>
        </>
      ),
    },
    {
      key: "universities",
      label: "Сургууль судалгаа",
      children: (
        <RepeatList
          name="universities"
          itemLabel="Сургууль"
          addLabel="Сургууль нэмэх"
          renderItem={(idx, rest) => (
            <>
              <Form.Item {...rest} name={[idx, "country"]} label="Улс">
                <Input />
              </Form.Item>
              <Form.Item {...rest} name={[idx, "name"]} label="Сургуулийн нэр">
                <Input />
              </Form.Item>
              <Form.Item {...rest} name={[idx, "reasons"]} label="Яагаад энэ сургууль руу өргөдөл илгээх вэ?">
                <TextArea autoSize={{ minRows: 3, maxRows: 14 }} />
              </Form.Item>
            </>
          )}
        />
      ),
    },
    {
      key: "exams",
      label: "Академик шалгалт",
      children: (
        <RepeatList
          name="exams"
          itemLabel="Шалгалт"
          addLabel="Шалгалт нэмэх"
          renderItem={(idx, rest) => (
            <>
              <Form.Item {...rest} name={[idx, "examType"]} label="Шалгалт">
                <Select options={opts("EXAM_TYPE")} />
              </Form.Item>
              <Form.Item {...rest} name={[idx, "taken"]} label="Өгсөн үү?" valuePropName="checked">
                <Switch checkedChildren="Тийм" unCheckedChildren="Үгүй" />
              </Form.Item>
              <Form.Item {...rest} name={[idx, "officialScore"]} label="Албан ёсны оноо">
                <Input />
              </Form.Item>
              <Form.Item {...rest} name={[idx, "mockScore"]} label="Жишиг (mock) оноо">
                <Input />
              </Form.Item>
            </>
          )}
        />
      ),
    },
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 pt-[80px] pb-32">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-primary">Олон талт үнэлгээ</h1>
        <Space>
          <Link to="/college-prep/report">
            <Button icon={<FileTextOutlined />}>Тайлан харах</Button>
          </Link>
        </Space>
      </div>
      <p className="text-secondary text-sm mb-6">
        Энэ бол их сургуулийн өргөдлийн цогц үнэлгээ. Ахлах ангийн турш аажмаар
        бөглөж, дунд нь хадгалж болно.
      </p>

      <Form form={form} layout="vertical" disabled={loading}>
        <Tabs tabPosition="left" items={items} />
      </Form>

      <div className="fixed bottom-0 inset-x-0 bg-white border-t shadow-menuShadow py-3 z-40">
        <div className="max-w-3xl mx-auto px-4 flex justify-end gap-3">
          <Button loading={saving} onClick={() => persist("DRAFT")}>
            Ноорог хадгалах
          </Button>
          <Button type="primary" loading={saving} onClick={() => persist("SUBMITTED")}>
            Илгээх
          </Button>
        </div>
      </div>
    </div>
  );
}

export default CollegePrepForm;
